// Detalhe da pendência: abre direto nas fotos dela, com histórico e respostas do cliente.
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { fmtDate, fmtDayMon, fmtTime } from '../lib/format';
import type { NcDetail, NcTipo } from '../lib/types';
import { ErrorState, GravPill, Loading, PhotoTile, Sheet } from './ui';
import { photoToLightbox, useLightbox } from './feedback';

const TIPO: Record<NcTipo, { label: string; cls: string }> = {
  aberta: { label: 'Aberta', cls: 'new' },
  persiste: { label: 'Persiste', cls: 'per' },
  resolvida: { label: 'Resolvida', cls: 'res' },
};

/** `base`: "/api/t/:tid/ncs" (equipe) ou "/api/portal/ncs" (cliente). */
export function NcSheet({ base, ncId, onClose, reportHref }: {
  base: string; ncId: string | null; onClose: () => void; reportHref?: (visitId: string) => string;
}) {
  const lightbox = useLightbox();
  const q = useQuery({ queryKey: ['nc', base, ncId], queryFn: () => api.get<NcDetail>(`${base}/${ncId}`), enabled: !!ncId });
  const d = q.data;
  return (
    <Sheet open={!!ncId} onClose={onClose} title={d?.nc.titulo ?? 'Pendência'} subtitle={d ? `${d.nc.restaurantNome} · ${d.nc.setor}` : undefined} wide>
      {q.isLoading && <Loading rows={2} height={120} />}
      {q.error && <ErrorState message={errMsg(q.error)} />}
      {d && (
        <>
          <div className="row wrap gap-6" style={{ marginBottom: 14 }}>
            <span className={`pill upper ${d.nc.aberta ? 'new' : 'res'}`}>{d.nc.aberta ? 'Em aberto' : 'Resolvida'}</span>
            <GravPill g={d.nc.gravidade} />
            <span className="pill neutral">desde {fmtDayMon(d.nc.openedAt)}</span>
          </div>

          <div className="label" style={{ marginBottom: 8 }}>Fotos da pendência</div>
          {d.photos.length ? (
            <div className="grid-photos" style={{ marginBottom: 16 }}>
              {d.photos.map((p) => (
                <PhotoTile key={p.id} p={p} stamp={`Visita nº ${p.visitNumero ?? '—'} · ${fmtDayMon(p.visitDate)} ${fmtTime(p.capturedAt)}`} onOpen={() => lightbox(photoToLightbox(p))} />
              ))}
            </div>
          ) : (
            <div className="empty" style={{ marginBottom: 16 }}>Nenhuma foto vinculada a esta pendência.</div>
          )}

          <div className="label" style={{ marginBottom: 8 }}>Histórico</div>
          <div className="stack" style={{ marginBottom: d.replies.length ? 16 : 0 }}>
            {d.history.map((h) => (
              <div key={h.visitId} className="row gap-10" style={{ padding: '8px 0', borderTop: '1px solid var(--divider-2)' }}>
                <span className={`pill upper ${TIPO[h.tipo].cls}`} style={{ width: 82, justifyContent: 'center' }}>{TIPO[h.tipo].label}</span>
                <span className="flex1 small">Visita nº {h.numero ?? '—'} · {fmtDate(h.date)}</span>
                {reportHref && <Link to={reportHref(h.visitId)} onClick={onClose} className="link-btn">Relatório →</Link>}
              </div>
            ))}
          </div>

          {d.replies.length > 0 && (
            <>
              <div className="label" style={{ marginBottom: 8 }}>Respostas do cliente</div>
              {d.replies.map((r, i) => (
                <div key={i} className="reply" style={{ fontSize: 13 }}>
                  <strong style={{ fontWeight: 500 }}>{r.autorNome}</strong> <span className="mono small">{fmtDate(r.createdAt)}</span>
                  <div style={{ marginTop: 3, whiteSpace: 'pre-wrap' }}>{r.texto}</div>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </Sheet>
  );
}
